import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import DramaCard from '@/components/base/DramaCard';
import Timeline from '@/components/base/Timeline';
import { useSEO } from '@/hooks/useSEO';
import { dramas } from '@/mocks/dramas';
import { STATUS_LABEL, STATUS_STYLE, TIMELINE_META, categoryName, formatHeat } from '@/utils/drama';

export default function DramaDetailPage() {
  const { id } = useParams<{ id: string }>();
  const drama = dramas.find((item) => item.id === id);

  const related = useMemo(() => {
    if (!drama) return [];
    return dramas
      .filter((item) => item.id !== drama.id && item.category === drama.category)
      .concat(dramas.filter((item) => item.id !== drama.id && item.category !== drama.category))
      .slice(0, 3);
  }, [drama]);

  const jsonLd = useMemo(() => {
    if (!drama) return undefined;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: drama.title,
        description: drama.summary,
        inLanguage: 'zh-CN',
        datePublished: drama.startedAt,
        dateModified: drama.updatedAt,
        articleSection: categoryName(drama.category),
        keywords: drama.tags.join(','),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页' },
          { '@type': 'ListItem', position: 2, name: '瓜田总览' },
          { '@type': 'ListItem', position: 3, name: drama.title },
        ],
      },
    ];
  }, [drama]);

  useSEO({
    title: drama
      ? `${drama.title} — 完整时间线与结局 | 西瓜黑料`
      : '事件不存在 | 西瓜黑料',
    description: drama
      ? `${drama.summary} 按时间线查看这出瓜的起始、后续、反转与结局，当前进度 ${drama.progress}%。`
      : '没有找到对应的吃瓜事件，返回瓜田总览继续追。',
    keywords: drama
      ? `西瓜黑料,${drama.tags.join(',')},西瓜黑料时间线,西瓜黑料反转,西瓜黑料结局`
      : '西瓜黑料',
    path: drama ? `/drama/${drama.id}` : '/dramas',
    jsonLd,
  });

  if (!drama) {
    return (
      <section className="px-4 pb-20 pt-28 md:px-8">
        <div className="mx-auto max-w-2xl rounded-lg border border-background-200 bg-background-50 px-6 py-20 text-center">
          <i className="ri-emotion-sad-line text-5xl text-background-400"></i>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
            这口瓜不见了
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            你访问的时间线可能已经调整过地址，或者从未存在。回瓜田里再挑一出接着追吧。
          </p>
          <Link
            to="/dramas"
            className="mt-7 inline-flex h-11 items-center whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回瓜田总览
          </Link>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="border-b border-background-200 bg-background-100 px-4 pb-8 pt-24 md:px-8 md:pb-10 md:pt-28">
        <div className="mx-auto max-w-6xl">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '瓜田总览', to: '/dramas' },
              { label: categoryName(drama.category), to: `/dramas?cat=${drama.category}` },
              { label: drama.title },
            ]}
          />

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full border px-3 py-1 text-xs font-medium ${STATUS_STYLE[drama.status]}`}
            >
              {STATUS_LABEL[drama.status]}
            </span>
            <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              {categoryName(drama.category)}
            </span>
            <span className="flex items-center gap-1 rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700">
              <i className="ri-fire-line text-primary-500"></i>
              热度 {formatHeat(drama.heat)}
            </span>
          </div>

          <h1 className="mt-4 max-w-3xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            {drama.title}
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {drama.summary}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              起始 {drama.startedAt}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-refresh-line"></i>
              最近更新 {drama.updatedAt}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-node-tree text-accent-600"></i>
              共 {drama.timeline.length} 个节点
            </span>
          </div>
        </div>
      </section>

      <section className="px-4 py-10 md:px-8 md:py-14">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="mb-8 w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src={drama.cover}
                alt={`${drama.title} 时间线配图`}
                className="h-56 w-full object-top md:h-80"
              />
            </div>

            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              完整时间线
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              从第一幕到最后更新，按日期先后排列。
            </p>

            <div className="mt-8">
              <Timeline entries={drama.timeline} />
            </div>

            <div className="mt-4 rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-900">
                <i className="ri-information-line text-primary-500"></i>
                进度说明
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                当前事件进度为 {drama.progress}%，状态标记为「{STATUS_LABEL[drama.status]}」。
                若后续出现新的反转或续集，我们会在此条时间线上继续追加节点，并同步更新进度与结局说明。
              </p>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-900">节点速览</h2>
                <ol className="mt-4 space-y-3">
                  {drama.timeline.map((entry, index) => {
                    const meta = TIMELINE_META[entry.type];
                    return (
                      <li key={`${entry.date}-${index}`}>
                        <a
                          href={`#timeline-node-${index}`}
                          className="group flex items-start gap-2.5 text-sm text-foreground-600 transition-colors hover:text-primary-600"
                        >
                          <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${meta.dotClass}`}></span>
                          <span className="min-w-0">
                            <span className="line-clamp-2 leading-snug group-hover:text-primary-600">
                              {entry.title}
                            </span>
                            <time className="mt-1 block text-xs text-foreground-400">
                              {entry.date} · {meta.label}
                            </time>
                          </span>
                        </a>
                      </li>
                    );
                  })}
                </ol>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-sm font-semibold text-foreground-900">事件进度</h2>
                <div className="mt-3 flex items-center justify-between text-xs text-foreground-500">
                  <span>当前进度</span>
                  <span className="font-medium text-foreground-800">{drama.progress}%</span>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-background-200">
                  <span
                    className="block h-full rounded-full bg-primary-500"
                    style={{ width: `${drama.progress}%` }}
                  ></span>
                </div>

                <h3 className="mt-5 text-sm font-semibold text-foreground-900">涉及方</h3>
                <ul className="mt-3 space-y-2">
                  {drama.participants.map((person) => (
                    <li
                      key={person}
                      className="flex items-center gap-2 text-sm text-foreground-600"
                    >
                      <i className="ri-user-3-line text-accent-600"></i>
                      {person}
                    </li>
                  ))}
                </ul>

                <h3 className="mt-5 text-sm font-semibold text-foreground-900">标签</h3>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {drama.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md border border-background-200 bg-background-100 px-2 py-0.5 text-xs text-foreground-600"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="bg-background-100 px-4 py-14 md:px-8 md:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="flex items-end justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              接着追这几出
            </h2>
            <Link
              to="/dramas"
              className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              更多时间线
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <DramaCard key={item.id} drama={item} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}